import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  LogOut, 
  Plus, 
  Edit, 
  Trash2, 
  Image as ImageIcon, 
  FileText, 
  Users, 
  Building2, 
  Film,
  Settings,
  ExternalLink,
  Bell,
  UserCog,
  Sliders,
  BarChart3,
  Eye,
  TrendingUp,
  Calendar,
  Mail,
  FileKey,
  Globe,
  Train
} from 'lucide-react';

// Helper for authenticated fetch (uses localStorage token as fallback)
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

// Helper function to convert Google Drive URLs to direct image URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  
  // Already converted
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  // Pattern: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    // Use thumbnail endpoint which is more reliable
    return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1000`;
  }
  
  // Pattern: https://drive.google.com/open?id=FILE_ID
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) {
    return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1000`;
  }
  
  // Pattern: https://drive.google.com/uc?id=FILE_ID or uc?export=view&id=FILE_ID
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) {
    return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w1000`;
  }
  
  // Not a Google Drive URL, return as-is
  return url;
};

// Helper function to convert Google Drive video URLs to embed URLs
const convertGoogleDriveVideoUrl = (url) => {
  if (!url) return url;
  
  // Already an embed URL
  if (url.includes('drive.google.com/file/d/') && url.includes('/preview')) return url;
  
  // Pattern: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;
  }
  
  // Pattern: https://drive.google.com/open?id=FILE_ID
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) {
    return `https://drive.google.com/file/d/${openMatch[1]}/preview`;
  }
  
  // Not a Google Drive URL, return as-is (YouTube, Vimeo, etc.)
  return url;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CMSDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [activeTab, setActiveTab] = useState('content');
  const [content, setContent] = useState([]);
  const [artists, setArtists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  // Check authentication
  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        // Try localStorage token first (for browsers that block cookies)
        const token = localStorage.getItem('session_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
          headers
        });
        
        if (!response.ok) {
          // Clear any stale data
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        setUser(userData);
      } catch (error) {
        navigate('/cms/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  // Fetch data
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const [contentRes, artistsRes, projectsRes, companiesRes] = await Promise.all([
          authFetch(`${API_URL}/api/content`),
          authFetch(`${API_URL}/api/artists`),
          authFetch(`${API_URL}/api/projects`),
          authFetch(`${API_URL}/api/companies`)
        ]);

        if (contentRes.ok) {
          const contentData = await contentRes.json();
          // Sort by date descending (most recent first)
          contentData.sort((a, b) => new Date(b.date) - new Date(a.date));
          setContent(contentData);
        }
        if (artistsRes.ok) setArtists(await artistsRes.json());
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (companiesRes.ok) setCompanies(await companiesRes.json());

        // Fetch pending requests count for admins
        if (user.role === 'admin') {
          const countRes = await authFetch(`${API_URL}/api/association-requests/pending/count`);
          if (countRes.ok) {
            const countData = await countRes.json();
            setPendingRequestsCount(countData.count);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear localStorage
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    navigate('/cms/login');
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await authFetch(`${API_URL}/api/${type}/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Refresh data
        if (type === 'content') {
          setContent(content.filter(c => c.content_id !== id));
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              CMS
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-8 h-8 rounded-full"
                />
              )}
              <div className="text-sm">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab('content')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left ${
                  activeTab === 'content' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                }`}
              >
                <FileText size={18} />
                Content
              </button>
              <button
                onClick={() => setActiveTab('artists')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left ${
                  activeTab === 'artists' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                }`}
              >
                <Users size={18} />
                Artists
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setActiveTab('projects')}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left ${
                      activeTab === 'projects' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                    }`}
                  >
                    <Film size={18} />
                    Projects
                  </button>
                  <button
                    onClick={() => setActiveTab('companies')}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left ${
                      activeTab === 'companies' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                    }`}
                  >
                    <Building2 size={18} />
                    Companies
                  </button>
                  
                  {/* Admin-only separator */}
                  <div className="my-2 border-t border-border"></div>
                  
                  <Link
                    to="/cms/brand-sites"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted text-primary"
                  >
                    <Globe size={18} />
                    Brand Sites
                  </Link>
                  <Link
                    to="/cms/btt"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted"
                    style={{ color: '#00A86B' }}
                  >
                    <Train size={18} />
                    BTT Dashboard
                  </Link>
                  <button
                    onClick={() => setActiveTab('analytics')}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted ${activeTab === 'analytics' ? 'bg-muted' : ''}`}
                  >
                    <BarChart3 size={18} />
                    Analytics
                  </button>
                  <Link
                    to="/cms/requests"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted"
                  >
                    <Bell size={18} />
                    Requests
                    {pendingRequestsCount > 0 && (
                      <span className="ml-auto px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                        {pendingRequestsCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/cms/users"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted"
                  >
                    <UserCog size={18} />
                    User Management
                  </Link>
                  <Link
                    to="/cms/settings"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted"
                  >
                    <Sliders size={18} />
                    Site Settings
                  </Link>
                  <Link
                    to="/cms/homepage-sections"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted"
                  >
                    <ImageIcon size={18} />
                    Homepage Backgrounds
                  </Link>
                  <Link
                    to="/cms/newsletter"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted"
                  >
                    <Mail size={18} />
                    Newsletter
                  </Link>
                  <Link
                    to="/cms/overview-settings"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted"
                  >
                    <FileKey size={18} />
                    Overview Page
                  </Link>
                  <Link
                    to="/cms/bulk-images"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-muted text-yellow-500"
                  >
                    <ImageIcon size={18} />
                    Fix Image URLs
                  </Link>
                </>
              )}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-4">
            {/* Analytics Tab */}
            {activeTab === 'analytics' && user?.role === 'admin' && (
              <AnalyticsDashboard />
            )}
            
            {/* Content Tab */}
            {activeTab === 'content' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl editorial-serif">Content Items</h2>
                  <Button onClick={() => { setEditingItem(null); setShowForm(true); }}>
                    <Plus size={16} className="mr-2" />
                    New Content
                  </Button>
                </div>

                {showForm ? (
                  <ContentForm
                    item={editingItem}
                    projects={projects}
                    artists={artists}
                    companies={companies}
                    onSave={async (data) => {
                      try {
                        // Convert Google Drive URLs before saving
                        const processedData = {
                          ...data,
                          image: convertGoogleDriveUrl(data.image),
                          og_image: convertGoogleDriveUrl(data.og_image),
                          video_url: convertGoogleDriveVideoUrl(data.video_url)
                        };
                        
                        const url = editingItem 
                          ? `${API_URL}/api/content/${editingItem.content_id}`
                          : `${API_URL}/api/content`;
                        const method = editingItem ? 'PUT' : 'POST';
                        
                        const response = await fetch(url, {
                          method,
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify(processedData)
                        });
                        
                        if (response.ok) {
                          const saved = await response.json();
                          if (editingItem) {
                            setContent(content.map(c => c.content_id === saved.content_id ? saved : c));
                          } else {
                            setContent([saved, ...content]);
                          }
                          setShowForm(false);
                          setEditingItem(null);
                        }
                      } catch (error) {
                        console.error('Save error:', error);
                      }
                    }}
                    onCancel={() => { setShowForm(false); setEditingItem(null); }}
                  />
                ) : (
                  <div className="space-y-4">
                    {content.map((item) => (
                      <Card key={item.content_id} className="p-4">
                        <div className="flex items-start gap-4">
                          {item.image && (
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-24 h-16 object-cover bg-muted"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary uppercase">
                                {item.type}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {item.source}
                              </span>
                            </div>
                            <h3 className="font-medium truncate">{item.title}</h3>
                            <p className="text-sm text-muted-foreground truncate">{item.dek}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setEditingItem(item); setShowForm(true); }}
                            >
                              <Edit size={14} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDelete('content', item.content_id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                            <Link to={`/${item.slug}`} target="_blank">
                              <Button variant="ghost" size="sm">
                                <ExternalLink size={14} />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Artists Tab */}
            {activeTab === 'artists' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl editorial-serif">Artists</h2>
                  {isAdmin && (
                    <Button onClick={() => navigate('/cms/artist/new')}>
                      <Plus size={16} className="mr-2" />
                      New Artist
                    </Button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {artists.map((artist) => (
                    <Card key={artist.artist_id} className="p-4">
                      <div className="flex items-start gap-4">
                        <img 
                          src={artist.image || '/placeholder.jpg'} 
                          alt={artist.name}
                          className="w-16 h-16 object-cover bg-muted"
                        />
                        <div className="flex-1">
                          <h3 className="font-medium">{artist.name}</h3>
                          <p className="text-sm text-muted-foreground">{artist.role}</p>
                          {(isAdmin || artist.user_id === user?.user_id) && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => navigate(`/cms/artist/${artist.artist_id}`)}
                            >
                              <Edit size={14} className="mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Projects Tab (Admin) */}
            {activeTab === 'projects' && isAdmin && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl editorial-serif">Projects</h2>
                  <Button onClick={() => navigate('/cms/project/new')}>
                    <Plus size={16} className="mr-2" />
                    New Project
                  </Button>
                </div>

                <div className="space-y-4">
                  {projects.map((project) => (
                    <Card key={project.project_id} className="p-4">
                      <div className="flex items-start gap-4">
                        <img 
                          src={project.image || '/placeholder.jpg'} 
                          alt={project.title}
                          className="w-24 h-16 object-cover bg-muted"
                        />
                        <div className="flex-1">
                          <h3 className="font-medium">{project.title}</h3>
                          <p className="text-sm text-muted-foreground">{project.tagline}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/cms/project/${project.project_id}`)}
                        >
                          <Edit size={14} />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Companies Tab (Admin) */}
            {activeTab === 'companies' && isAdmin && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl editorial-serif">Companies</h2>
                  <Button onClick={() => navigate('/cms/company/new')}>
                    <Plus size={16} className="mr-2" />
                    New Company
                  </Button>
                </div>

                <div className="space-y-4">
                  {companies.map((company) => (
                    <Card key={company.company_id} className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-muted flex items-center justify-center">
                          {company.logo ? (
                            <img src={company.logo} alt={company.name} className="max-w-full max-h-full object-contain" />
                          ) : (
                            <Building2 size={24} className="text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium">{company.name}</h3>
                          <p className="text-sm text-muted-foreground">{company.tagline}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/cms/company/${company.company_id}`)}
                        >
                          <Edit size={14} />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

// Content Form Component
const ContentForm = ({ item, projects, artists, companies, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    slug: item?.slug || '',
    dek: item?.dek || '',
    body: item?.body || '',
    video_url: item?.video_url || '',
    type: item?.type || 'Project',
    category: item?.category || 'Update',
    project: item?.project || '',
    artist: item?.artist || '',
    company: item?.company || '',
    author: item?.author || '',
    date: item?.date || new Date().toISOString().split('T')[0],
    image: item?.image || '',
    source: item?.source || 'Cultural Revolution',
    external_url: item?.external_url || '',
    featured: item?.featured || false,
    site_scope: item?.site_scope || ['cr'],
    background_image: item?.background_image || '',
    status: item?.status || 'published',
    publish_date: item?.publish_date || '',
    seo_title: item?.seo_title || '',
    seo_description: item?.seo_description || '',
    og_image: item?.og_image || ''
  });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (response.ok) {
        const result = await response.json();
        setFormData({ ...formData, image: `${API_URL}${result.url}` });
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">{item ? 'Edit Content' : 'New Content'}</h3>
      
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Slug</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
              placeholder="auto-generated if empty"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Dek (Short Description) *</label>
          <textarea
            value={formData.dek}
            onChange={(e) => setFormData({ ...formData, dek: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded"
            rows={2}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Video URL</label>
          <input
            type="url"
            value={formData.video_url}
            onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded"
            placeholder="YouTube, Vimeo, or Google Drive video link"
          />
          <p className="text-xs text-muted-foreground mt-1">Supports: YouTube, Vimeo, Google Drive (auto-converted). Video appears above body text.</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Body Content</label>
          <p className="text-xs text-muted-foreground mb-2">
            Use the editor below for formatted text. Supports headings, bold, italic, links, lists, images, and video embeds.
          </p>
          <div className="bg-background border border-border rounded overflow-hidden">
            <ReactQuill
              theme="snow"
              value={formData.body}
              onChange={(value) => setFormData({ ...formData, body: value })}
              placeholder="Write your article content here..."
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['blockquote'],
                  ['link', 'image', 'video'],
                  [{ 'align': [] }],
                  ['clean']
                ]
              }}
              formats={[
                'header',
                'bold', 'italic', 'underline', 'strike',
                'list', 'bullet',
                'blockquote',
                'link', 'image', 'video',
                'align'
              ]}
              style={{ minHeight: '300px' }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Tip: For images, use Google Drive links. For videos, paste YouTube/Vimeo URLs.
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
            >
              <option value="Project">Project</option>
              <option value="Press">Press</option>
              <option value="Artist">Artist</option>
              <option value="Company">Company</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
            >
              <option value="Press">Press</option>
              <option value="Release">Release</option>
              <option value="Update">Update</option>
              <option value="Essay">Essay</option>
              <option value="BTS">BTS</option>
              <option value="Announcement">Announcement</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Source</label>
            <input
              type="text"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Project</label>
            <select
              value={formData.project}
              onChange={(e) => setFormData({ ...formData, project: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
            >
              <option value="">Select a project (optional)</option>
              {projects.map((p) => (
                <option key={p.project_id} value={p.title}>{p.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Artist</label>
            <select
              value={formData.artist}
              onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
            >
              <option value="">Select an artist (optional)</option>
              {artists.map((a) => (
                <option key={a.artist_id} value={a.name}>{a.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Company</label>
            <select
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
            >
              <option value="">Select a company (optional)</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Author Byline</label>
            <input
              type="text"
              value={formData.author || ''}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
              placeholder="Author name for article byline"
            />
            <p className="text-xs text-muted-foreground mt-1">Displays as &quot;By [Author Name]&quot; on the article</p>
          </div>
        </div>

        {/* Site Scope - Multi-site publishing */}
        <div>
          <label className="block text-sm font-medium mb-1">Publish To Sites</label>
          <div className="flex flex-wrap gap-4 p-3 bg-muted/30 rounded">
            {[
              { value: 'cr', label: 'Cultural Revolution' },
              { value: 'selina', label: 'Selina Ringel' },
              { value: 'bethetrain', label: 'Be The Train' }
            ].map((site) => (
              <label key={site.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(formData.site_scope || ['cr']).includes(site.value)}
                  onChange={(e) => {
                    const currentScope = formData.site_scope || ['cr'];
                    const newScope = e.target.checked
                      ? [...currentScope, site.value]
                      : currentScope.filter(s => s !== site.value);
                    setFormData({ ...formData, site_scope: newScope.length > 0 ? newScope : ['cr'] });
                  }}
                  className="rounded"
                />
                <span className="text-sm">{site.label}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Content will appear on selected sites</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">External URL</label>
          <input
            type="url"
            value={formData.external_url}
            onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded"
            placeholder="https://..."
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium mb-1">Featured Image</label>
          <div className="flex items-start gap-4">
            {formData.image && (
              <img src={convertGoogleDriveUrl(formData.image)} alt="Preview" className="w-32 h-20 object-cover bg-muted" />
            )}
            <div className="flex-1">
              <input
                type="text"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded mb-2"
                placeholder="Paste image URL or Google Drive share link"
              />
              <p className="text-xs text-muted-foreground mb-2">
                Supports: Direct URLs, Google Drive links (auto-converted)
              </p>
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-border cursor-pointer hover:bg-muted">
                <ImageIcon size={16} />
                {uploading ? 'Uploading...' : 'Or Upload Image'}
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </div>
        </div>

        {/* Publishing Settings */}
        <div className="border-t border-border pt-4 mt-4">
          <h4 className="font-medium mb-3">Publishing</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
            {formData.status === 'scheduled' && (
              <div>
                <label className="block text-sm font-medium mb-1">Publish Date</label>
                <input
                  type="date"
                  value={formData.publish_date}
                  onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-muted-foreground mt-1">Content will be visible on this date</p>
              </div>
            )}
          </div>
        </div>

        {/* SEO Fields */}
        <div className="border-t border-border pt-4 mt-4">
          <h4 className="font-medium mb-3">SEO Settings</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">SEO Title</label>
              <input
                type="text"
                value={formData.seo_title}
                onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                placeholder="Custom title for search engines (default: title)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">SEO Description</label>
              <textarea
                value={formData.seo_description}
                onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                rows={2}
                placeholder="Custom description for search engines (default: dek)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">OG Image URL</label>
              <input
                type="text"
                value={formData.og_image}
                onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                placeholder="Custom image for social sharing (default: featured image)"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="featured"
            checked={formData.featured}
            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
          />
          <label htmlFor="featured" className="text-sm">Featured content</label>
        </div>

        <div className="flex items-center gap-4 pt-4">
          <Button onClick={() => onSave(formData)}>
            {item ? 'Update' : 'Create'} Content
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/overview`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAnalytics(data);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-muted-foreground">Unable to load analytics</p>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl editorial-serif">Analytics Dashboard</h2>
      
      {/* Overview Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <FileText className="text-primary" size={24} />
            <div>
              <p className="text-2xl font-bold">{analytics.total_content}</p>
              <p className="text-sm text-muted-foreground">Total Content</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Eye className="text-primary" size={24} />
            <div>
              <p className="text-2xl font-bold">{analytics.total_views.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Total Views</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="text-primary" size={24} />
            <div>
              <p className="text-2xl font-bold">{analytics.recent_content_count}</p>
              <p className="text-sm text-muted-foreground">Last 30 Days</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Calendar className="text-primary" size={24} />
            <div>
              <p className="text-2xl font-bold">{analytics.by_status?.scheduled || 0}</p>
              <p className="text-sm text-muted-foreground">Scheduled</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Top Content */}
      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4">Top Performing Content</h3>
        <div className="space-y-3">
          {analytics.top_content?.map((item, index) => (
            <div key={item.content_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <div className="flex items-center gap-3">
                <span className="text-muted-foreground w-6">{index + 1}.</span>
                <div>
                  <p className="font-medium">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Eye size={14} />
                <span>{(item.view_count || 0).toLocaleString()}</span>
              </div>
            </div>
          ))}
          {(!analytics.top_content || analytics.top_content.length === 0) && (
            <p className="text-muted-foreground text-sm">No content views tracked yet</p>
          )}
        </div>
      </Card>

      {/* Content by Category */}
      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4">Content by Category</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.entries(analytics.by_category || {}).map(([category, count]) => (
            <div key={category} className="flex items-center justify-between p-3 bg-muted/30 rounded">
              <span className="text-sm">{category}</span>
              <span className="font-bold">{count}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Status Breakdown */}
      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4">Content Status</h3>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-sm">Published: {analytics.by_status?.published || analytics.by_status?.null || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-sm">Scheduled: {analytics.by_status?.scheduled || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
            <span className="text-sm">Draft: {analytics.by_status?.draft || 0}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CMSDashboard;
